import React, { useRef, useEffect } from 'react';
import CgDialog from 'modules/Common/ui/CgDialog';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import CgButton from 'modules/Common/ui/CgButton';
import { useTranslation } from 'react-i18next';
import renderSimpleMarkdown from '../utils/renderSimpleMarkdown';
import { useBranding } from 'modules/App/hooks/useBranding';
import { resolveVideo } from './OnboardingVideoLightbox';

const GuidingChecklistItemModal = ({ show, onHide, item, onTick, isTickLoading }) => {
  const { t } = useTranslation('onboarding');
  const history = useHistory();

  const stepsRef = useRef(null);

  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return;

    const handleClick = (e) => {
      const link = e.target.closest('a[data-internal-link]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        onHide();
        history.push(href);
      }
    };

    // Capture phase so we intercept before the anchor's native navigation
    el.addEventListener('click', handleClick, true);
    return () => el.removeEventListener('click', handleClick, true);
  }, [show, history, onHide]);

  const brand = useBranding();

  if (!item) return null;

  // The checklist walkthroughs are CG-branded content — hidden on whitelabel
  // builds. Which host they sit on is resolveVideo's problem, not this file's.
  const video = brand.key === 'carbonglance' ? resolveVideo(item.videoUrl) : null;

  return (
    <CgDialog
      open={show}
      onOpenChange={(next) => {
        if (!next) onHide();
      }}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          {item.completed ? (
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-primaryColor"
            />
          ) : (
            <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-gray-400">{item._index + 1}</span>
            </div>
          )}
          <span className={item.completed ? 'text-gray-400' : ''}>{item.name}</span>
          {item.completed && (
            <span className="text-[10px] font-bold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-full ml-2">
              {t('checklist.doneBadge', 'DONE')}
            </span>
          )}
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <CgButton
            variant="info"
            label={t('common:actions.close', 'Close')}
            onClick={onHide}
          />
          {!item.completed && (
            <CgButton
              variant="primary"
              icon="faCheck"
              label={t('checklist.markComplete', 'Mark Complete')}
              onClick={() => onTick(item.key)}
              loading={isTickLoading}
            />
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Description */}
        {item.description && <p className="text-sm text-gray-600">{item.description}</p>}

        {/* Steps (rendered markdown) */}
        {item.steps && (
          <div
            ref={stepsRef}
            className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-4"
            dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(item.steps) }}
          />
        )}

        {/* The walkthrough. No autoplay here, unlike the lightbox — this modal
            opens for the step, not for the video, so it must not start talking
            at someone who came to read the how-to. */}
        {video && (
          <div className="aspect-video w-full rounded-lg overflow-hidden border border-gray-200 bg-black">
            {video.kind === 'file' ? (
              <video
                src={video.src}
                className="w-full h-full"
                controls
                playsInline
              >
                <track kind="captions" />
              </video>
            ) : (
              <iframe
                src={video.src}
                title={item.name}
                className="w-full h-full"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        )}
      </div>
    </CgDialog>
  );
};

export default GuidingChecklistItemModal;
